import Box from "@mui/material/Box";
import { useTheme } from "@mui/material";
import isNil from "lodash/isNil";
import React, { useMemo } from "react";
import Flx from "../../layout/Flx";
import Htag from "../../typography/Htag";
import zIndex from "@mui/material/styles/zIndex";

/**
 * TitledGroup Component
 * @param {Object} props
 * @param {string} props.title - The title text
 * @param {React.ReactNode} props.children - Child components
 * @param {boolean} props.fw - Full width flag
 * @param {Object} props.sx - Style overrides for root
 * @param {Object} props.bodySx - Style overrides for body
 * @param {React.ReactNode} props.icon - Optional icon to display
 * @param {Object} props.headerSx - Style overrides for header
 * @param {React.ReactNode} props.headerEndContent - Optional content to display at end of header
 * @param {boolean} props.divider - Whether to show divider beneath header
 * @param {string} props.variant - Title size variant ('h2'|'h3'|'h4'), default 'h3'
 * @param {boolean} props.suppressPadding - Whether to remove default padding
 * @param {boolean} props.suppressBodyPadding - Whether to remove default padding
 */

const paddingAmt = 2;
const TitledGroup = ({
  title,
  children,
  fw,
  sx,
  icon,
  bodySx,
  headerSx,
  headerEndContent,
  divider,
  variant = "h3",
  suppressPadding,
  fontWeight = 800,
  // fontWeight = 500,
  suppressHeaderPadding,
  uppercase,
  suppressBodyPadding,
  headerBottomPadding,
  onHeaderClick,
  spt,
  spb,
  spl,
  spr,
}) => {
  const cardRootStyles = useMemo(
    () => ({
      flexGrow: 1,

      ...sx,
      ...(fw && { width: "100%" }),
      ...(suppressPadding && { padding: 0 }),
    }),
    [fw, sx, suppressPadding],
  );

  return (
    <Box sx={cardRootStyles} className="titled-group-root">
      <TitledGroupHeader
        sx={headerSx}
        divider={divider}
        icon={icon}
        variant={variant}
        uppercase={uppercase}
        title={title}
        fontWeight={fontWeight}
        suppressHeaderPadding={suppressHeaderPadding}
        headerEndContent={headerEndContent}
        onHeaderClick={onHeaderClick}
        headerBottomPadding={headerBottomPadding}
      />
      <BodyContent
        suppressBodyPadding={suppressBodyPadding}
        sx={bodySx}
        spt={spt}
        spb={spb}
        spl={spl}
        spr={spr}
      >
        {children}
      </BodyContent>
    </Box>
  );
};

const TitledGroupHeader = ({
  divider,
  sx,
  icon,
  headerEndContent,
  variant,
  title,
  fontWeight,
  suppressHeaderPadding,
  headerBottomPadding,
  uppercase,
  onHeaderClick,
}) => {
  if (isNil(title) && isNil(headerEndContent)) return null;
  return (
    <TitledGroupHeaderRoot
      headerEndContent={headerEndContent}
      sx={sx}
      onHeaderClick={onHeaderClick}
      divider={divider}
      suppressHeaderPadding={suppressHeaderPadding}
      headerBottomPadding={headerBottomPadding}
    >
      <Flx
        ac
        sx={{
          display: "flex",
          gap: 0.8,
          ".MuiSvgIcon-root": {
            fontSize: "16px !important",
          },
        }}
        className={"titled-group-header-title-content"}
      >
        {icon && <Icon>{icon}</Icon>}
        <Title fontWeight={fontWeight} uppercase={uppercase} variant={variant}>
          {title}
        </Title>
      </Flx>
    </TitledGroupHeaderRoot>
  );
};

const TitledGroupHeaderRoot = ({
  sx,
  divider,
  children,
  headerEndContent,
  suppressHeaderPadding,
  headerBottomPadding = 0,
  onHeaderClick,
}) => {
  const styles = useMemo(
    () => ({
      px: suppressHeaderPadding ? 0 : paddingAmt,
      pt: suppressHeaderPadding ? 0 : paddingAmt + 1,
      pb: divider ? 1.5 : headerBottomPadding,
      zIndex: 11,
      position: "relative",
      color: "#232a31",
      cursor: onHeaderClick ? "pointer" : "default",
      // color: "#1e293b",
      // color: "#656e76",
      borderBottom: divider
        ? `1px solid #f1f5f9`
        : // ? `1px solid ${theme.palette.dividerLight}`
          "none",
      ...sx,
    }),
    [sx, divider, onHeaderClick, suppressHeaderPadding, headerBottomPadding],
  );

  return (
    <Box
      sx={styles}
      className="titled-group-header-root"
      onClick={onHeaderClick}
    >
      {headerEndContent ? (
        <Flx fw jb wrap ac>
          {children}
          {headerEndContent}
        </Flx>
      ) : (
        children
      )}
    </Box>
  );
};

const Icon = ({ children }) => {
  if (isNil(children)) return null;

  return (
    <Flx
      center
      sx={{
        color: "inherit",
        svg: {
          color: "inherit",
        },
      }}
    >
      {children}
    </Flx>
  );
};

const Title = ({ children, variant, uppercase, fontWeight }) => {
  if (isNil(children)) return null;

  return (
    <Htag
      sx={{
        color: "inherit",
        textTransform: uppercase ? "uppercase" : "capitalize",
        margin: 0,
        fontWeight: fontWeight,
      }}
      variant={variant}
    >
      {children}
    </Htag>
  );
};

const BodyContent = ({
  suppressBodyPadding,
  sx,
  children,
  spt,
  spb,
  spl,
  spr,
}) => {
  const styles = useMemo(() => {
    const p = suppressBodyPadding ? 0 : paddingAmt;
    return {
      position: "relative",
      pt: spt ? 0 : p,
      pb: spb ? 0 : p,
      pl: spl ? 0 : p,
      pr: spr ? 0 : p,
      ...sx,
    };
  }, [sx, suppressBodyPadding, spt, spb, spl, spr]);

  if (isNil(children)) return null;
  return (
    <Box className="titled-group-body-root" sx={styles}>
      {children}
    </Box>
  );
};

export default TitledGroup;
